import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {factionCardOverlayToggled, policyCardOverlayToggled} from '../redux/actions';
import {SIDE_MENU_IDS, useSideMenuStack} from './SideMenuStackContext';

const PlayersListSideMenuContext = createContext(null);

// Откуда открыта карточка фракции или политики — обучение проверяет «открой
// карточку таким-то способом» (factionCardViewedFrom, policyCardViewedFrom)
export const CARD_OPEN_SOURCES = Object.freeze({
  PLAYERS_PANEL: 'players_panel',
  COMPLETE_BUTTON: 'complete_button',
  ACTING_BADGE: 'acting_badge',
});

export const PlayersListSideMenuProvider = ({children}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [cardOpenSource, setCardOpenSource] = useState(CARD_OPEN_SOURCES.PLAYERS_PANEL);
  const dispatch = useDispatch();
  const {topSideMenuId, bringSideMenuToFront} = useSideMenuStack();

  // Панель игроков ушла под другую (например, клик по вкладке рынка политик,
  // который заодно скрывает открытую карточку) — открытые из нее карточки
  // фракции/политики закрываются, а не висят смонтированными под верхней
  // панелью. Транзиция закрытия уйдет в стор штатными эффектами ниже —
  // задание обучения «открой и закрой карточку» засчитывается и таким путем
  // Только при открытом списке: карточка, открытая с кнопки хода, списка не
  // открывает и не должна закрываться оттого, что сверху стопки рынок
  useEffect(() => {
    if (isMenuOpen && topSideMenuId !== SIDE_MENU_IDS.PLAYERS_LIST) {
      setSelectedPlayer(null);
      setSelectedPolicy(null);
    }
  }, [isMenuOpen, topSideMenuId]);

  // Карточка закрыта — источник «с кнопки хода» больше не действует: иначе
  // следующая карточка, открытая из списка, ушла бы в стор с чужим источником
  useEffect(() => {
    if (!selectedPlayer) {
      setCardOpenSource(CARD_OPEN_SOURCES.PLAYERS_PANEL);
    }
  }, [selectedPlayer]);

  // Карточка фракции (FactionCardOverlay из PlayersList, порталом на весь
  // экран) открыта, когда выбран
  // игрок без выбранной политики. Транзиции открытия/закрытия дублируются в
  // стор с источником — обучение проверяет «открой карточку обоими способами»
  const isFactionCardOpen = Boolean(selectedPlayer) && !selectedPolicy;
  useEffect(() => {
    dispatch(factionCardOverlayToggled(
      isFactionCardOpen,
      isFactionCardOpen ? cardOpenSource : null,
    ));
  }, [isFactionCardOpen, cardOpenSource, dispatch]);

  // То же для карточки политики (PolicyCardOverlay в PlayersList: выбраны и
  // игрок, и политика)
  const isPolicyCardOpen = Boolean(selectedPlayer) && Boolean(selectedPolicy);
  useEffect(() => {
    dispatch(policyCardOverlayToggled(
      isPolicyCardOpen,
      isPolicyCardOpen ? cardOpenSource : null,
    ));
  }, [isPolicyCardOpen, cardOpenSource, dispatch]);

  const openMenu = useCallback(() => {
    setSelectedPlayer(null);
    setSelectedPolicy(null);
    setIsMenuOpen(true);
    bringSideMenuToFront(SIDE_MENU_IDS.PLAYERS_LIST);
  }, [bringSideMenuToFront]);

  // Карточка фракции (policy не задана) или политики игрока поверх всего
  // экрана — без открытия списка. source — откуда открыта, для обучения
  // («открой карточку с кнопки хода», CARD_OPEN_SOURCES)
  const openCard = useCallback((player, policy = null, source = CARD_OPEN_SOURCES.PLAYERS_PANEL) => {
    setSelectedPlayer(player);
    setSelectedPolicy(policy);
    setCardOpenSource(source);
  }, []);

  // Закрытый список сбрасывает и открытые из него карточки
  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
    setSelectedPlayer(null);
    setSelectedPolicy(null);
  }, []);

  // Строка игроков в шапке: открытую панель повторное нажатие задвигает,
  // даже если поверх лежит рынок. Возвращает, открыта ли панель после нажатия
  const toggleMenu = useCallback(() => {
    if (isMenuOpen) {
      closeMenu();
      return false;
    }
    openMenu();
    return true;
  }, [isMenuOpen, closeMenu, openMenu]);

  return (
    <PlayersListSideMenuContext.Provider
      value={{
        isMenuOpen,
        selectedPlayer,
        selectedPolicy,
        setSelectedPlayer,
        setSelectedPolicy,
        cardOpenSource,
        openMenu,
        openCard,
        closeMenu,
        toggleMenu,
      }}
    >
      {children}
    </PlayersListSideMenuContext.Provider>
  );
};

export const usePlayersListSideMenu = () => {
  const context = useContext(PlayersListSideMenuContext);
  if (!context) {
    throw new Error('usePlayersListSideMenu must be used within a PlayersListSideMenuProvider');
  }
  return context;
};
